import {useState, useEffect, useCallback} from 'react';
import debounce from 'lodash/debounce';
import {apiClient} from '../foundation/apiClient';

interface AppEvent {
    eventName: string;
    props: Record<string, string>;
}

interface AppTrackingEvents {
    events: AppEvent[];
}

export const useEventBatching = () => {
    const [batchedEvents, setBatchedEvents] = useState<AppEvent[]>([]);
    const [error, setError] = useState<string | null>(null);

    const debouncedSendEvents = useCallback(
        debounce(async () => {
            if (batchedEvents.length === 0) {
                return;
            }

            setError(null);

            try {
                const finalEvents: AppTrackingEvents = {
                    events: batchedEvents,
                };
                await apiClient.post('tracking/v1', finalEvents);
            } catch (err: any) {
                setError(err.message || 'An error occurred.');
            } finally {
                setBatchedEvents([]);
            }
        }, 2000),
        [batchedEvents],
    );

    const trackEvent = useCallback((event: AppEvent) => {
        setBatchedEvents((prevEvents) => [...prevEvents, event]);
    }, []); // Empty dependency array for useCallback

    useEffect(() => {
        debouncedSendEvents();
        return () => debouncedSendEvents.cancel();
    }, [batchedEvents, debouncedSendEvents]); // Include debouncedSendEvents in the dependencies

    return {trackEvent, error};
};
